import Client, { Network } from "@helium/http";
import moment from "moment";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

interface ContextValue {
  chainVars: ChainVars | undefined;
  blockchainHaltMoment: moment.Moment | undefined;
}

const HeliumDataContext = React.createContext<ContextValue | undefined>(
  undefined
);

const HeliumDataProvider = ({ children }: PropsWithChildren<{}>) => {
  const [chainVars, setChainVars] = useState<ChainVars | undefined>();

  const fetchChainVars = useCallback(async () => {
    const network = new Network({
      baseURL: process.env.REACT_APP_HELIUM_API_BASE_URL as string,
      version: 1
    });
    const client = new Client(network);
    const vars: ChainVars = await client.vars.getAll();
    setChainVars(vars);
  }, []);

  useEffect(() => {
    fetchChainVars();
  }, [fetchChainVars]);

  const value = useMemo(
    () => ({
      chainVars,
      blockchainHaltMoment: undefined // example: moment("2022-10-27 04:54:15+01")
    }),
    [chainVars]
  );

  return (
    <HeliumDataContext.Provider value={value}>
      {children}
    </HeliumDataContext.Provider>
  );
};

const useHeliumData = (): ContextValue => {
  const context = useContext(HeliumDataContext);
  if (context === undefined) {
    throw new Error("useHeliumData must be used within an HeliumDataProvider");
  }
  return context;
};

export { useHeliumData, HeliumDataProvider };

//
// Utils
//

export interface ChainVars {
  allowZeroAmount: false;
  alphaDecay: number;
  batchSize: number;
  betaDecay: number;
  blockTime: number;
  blockVersion: string;
  chainVarsVersion: number;
  consensusPercent: number;
  dataAggregationVersion: number;
  dcPayloadSize: number;
  dcPercent: number;
  densityTgtRes: number;
  dkgCurve: string;
  electionBbaPenalty: number;
  electionClusterRes: number;
  electionInterval: number;
  electionRemovalPct: number;
  electionReplacementFactor: number;
  electionReplacementSlope: number;
  electionRestartInterval: number;
  electionSeenPenalty: number;
  electionSelectionPct: number;
  electionVersion: number;
  h3ExclusionRingDist: number;
  h3MaxGridDistance: number;
  h3NeighborRes: number;
  hip17InteractivityBlocks: number;
  hip17Res0: [number, number, number];
  hip17Res10: [number, number, number];
  hip17Res11: [number, number, number];
  hip17Res12: [number, number, number];
  hip17Res1: [number, number, number];
  hip17Res2: [number, number, number];
  hip17Res3: [number, number, number];
  hip17Res4: [number, number, number];
  hip17Res5: [number, number, number];
  hip17Res6: [number, number, number];
  hip17Res7: [number, number, number];
  hip17Res8: [number, number, number];
  hip17Res9: [number, number, number];
  maxOpenSc: number;
  maxPayments: number;
  maxStaleness: number;
  maxSubnetNum: number;
  maxSubnetSize: number;
  maxXorFilterNum: number;
  maxXorFilterSize: number;
  minAssertH3Res: number;
  minExpireWithin: number;
  minScore: number;
  minSubnetSize: number;
  monthlyReward: number;
  nonce: number;
  numConsensusMembers: number;
  pocCentralityWt: number;
  pocChallengeInterval: number;
  pocChallengeSyncInterval: number;
  pocChallengeesPercent: number;
  pocChallengerType: string;
  pocChallengersPercent: number;
  pocGoodBucketHigh: number;
  pocGoodBucketLow: number;
  pocMaxHopCells: number;
  pocPathLimit: number;
  pocPerHopMaxWitnesses: number;
  pocRewardDecayRate: number;
  pocTargetHexParentRes: number;
  pocTypoFixes: true;
  pocV4ExclusionCells: number;
  pocV4ParentRes: number;
  pocV4ProbBadRssi: number;
  pocV4ProbCountWt: number;
  pocV4ProbGoodRssi: number;
  pocV4ProbNoRssi: number;
  pocV4ProbRssiWt: number;
  pocV4ProbTimeWt: number;
  pocV4RandomnessWt: number;
  pocV4TargetChallengeAge: number;
  pocV4TargetExclusionCells: number;
  pocV4TargetProbEdgeWt: number;
  pocV4TargetProbScoreWt: number;
  pocV4TargetScoreCurve: number;
  pocV5TargetProbRandomnessWt: number;
  pocVersion: number;
  pocWitnessConsiderationLimit: number;
  pocWitnessesPercent: number;
  predicateCallbackFun: string;
  predicateCallbackMod: string;
  predicateThreshold: number;
  priceOracleHeightDelta: number;
  priceOraclePriceScanDelay: number;
  priceOraclePriceScanMax: number;
  priceOraclePublicKeys: string[];
  priceOracleRefreshInterval: number;
  rewardVersion: number;
  scCausalityFix: number;
  scGcInterval: number;
  scGraceBlocks: number;
  scOpenValidationBugfix: number;
  scOvercommit: number;
  scVersion: number;
  securitiesPercent: number;
  snapshotInterval: number;
  snapshotVersion: number;
  stakingFeeTxnAddGatewayV1: number;
  stakingFeeTxnAssertLocationV1: number;
  stakingFeeTxnOuiV1: number;
  stakingFeeTxnOuiV1PerAddress: number;
  stakingKeys: string[];
  transferHotspotStalePocBlocks: number;
  txnFeeMultiplier: number;
  txnFees: true;
  varGwInactivityThreshold: number;
  varsCommitDelay: number;
  witnessRedundancy: number;
  witnessRefreshInterval: number;
  witnessRefreshRandN: number;
}
